import { assetManifest } from "../assets/manifest";
import { GAME_FRAME, OVERDRIVE_RULES } from "../config/gameplay";
import { polarityWindowCenterDegrees } from "../game/arena/shells";
import { overdriveZoneCenters } from "../game/events/overdrive";
import type { CoreState, GameSnapshot, ShellPolarityWindow, ShellState } from "../game/runtime/types";
import type { OrbitForgeRuntime } from "../game/runtime/runtime";
import { drawCorePattern } from "./core-patterns";
import { createLuckySparkLayer } from "./lucky-spark";
import { drawReactorStorm } from "./reactor-storm";
import { shellSkinPalette, type ShellSkinPalette } from "./shell-skins";

const coreColors = {
  cyan: "#35f0ff",
  pink: "#ff3ba7",
  lime: "#70ff8a",
  gold: "#ffd34a"
} as const;

const coreSpriteImage = new Image();
coreSpriteImage.src = assetManifest.coreSprites.url;

const coreSpriteIndex: Record<CoreState["color"], number> = {
  cyan: 0,
  pink: 1,
  lime: 2,
  gold: 3
};

const CORE_SPRITE_COLUMNS = 4;
const REACTOR_FIELD_RADIUS = 430;

let reactorFieldCache: HTMLCanvasElement | null = null;
const colorAlphaCache = new Map<string, string>();

export function createArena(runtime: OrbitForgeRuntime): HTMLElement {
  const arena = document.createElement("section");
  arena.className = "of-arena";
  arena.setAttribute("aria-label", "Reactor arena");

  const canvas = document.createElement("canvas");
  canvas.className = "of-arena-canvas";
  canvas.width = GAME_FRAME.width;
  canvas.height = GAME_FRAME.height;

  const feedbackLayer = document.createElement("div");
  feedbackLayer.className = "of-feedback-layer";

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Orbit requires a 2D canvas context.");
  }

  let seenFeedbackId = 0;
  runtime.subscribe((snapshot) => {
    renderArena(context, snapshot);
    seenFeedbackId = renderFeedback(feedbackLayer, snapshot, seenFeedbackId);
  });

  arena.addEventListener("pointerdown", (event) => {
    const frame = arena.closest<HTMLElement>(".of-game-frame");
    if (!frame) {
      return;
    }

    const rect = frame.getBoundingClientRect();
    const frameX = ((event.clientX - rect.left) / rect.width) * GAME_FRAME.width;
    const frameY = ((event.clientY - rect.top) / rect.height) * GAME_FRAME.height;
    runtime.dispatch({ type: "launch-core", frameX, frameY, source: "manual" });
  });

  arena.append(canvas, createLuckySparkLayer(runtime), feedbackLayer);
  return arena;
}

function renderArena(context: CanvasRenderingContext2D, snapshot: GameSnapshot): void {
  context.clearRect(0, 0, GAME_FRAME.width, GAME_FRAME.height);
  context.save();
  context.translate(GAME_FRAME.width / 2, GAME_FRAME.height / 2);
  drawReactorField(context);
  const shellPalette = shellSkinPalette(snapshot.save.cosmetics.SelectedShellSkin);
  context.save();
  if (snapshot.events.storm.active) {
    context.globalAlpha = 0.32;
  }
  for (const shell of snapshot.arena.shells) {
    drawShell(context, shell, shellPalette, snapshot.events.overdrive.active, snapshot.events.overdrive.zoneMultiplier);
  }
  context.restore();
  drawReactorStorm(context, snapshot.events.storm);
  for (const core of snapshot.arena.cores) {
    drawCore(context, core, snapshot.effects.polarityStackCap, snapshot.save.cosmetics.SelectedCorePattern);
  }
  context.restore();
}

function drawReactorField(context: CanvasRenderingContext2D): void {
  context.drawImage(reactorFieldCanvas(), -REACTOR_FIELD_RADIUS, -REACTOR_FIELD_RADIUS);
}

function reactorFieldCanvas(): HTMLCanvasElement {
  if (reactorFieldCache) {
    return reactorFieldCache;
  }

  const canvas = document.createElement("canvas");
  canvas.width = REACTOR_FIELD_RADIUS * 2;
  canvas.height = REACTOR_FIELD_RADIUS * 2;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Orbit requires a 2D canvas context.");
  }

  context.translate(REACTOR_FIELD_RADIUS, REACTOR_FIELD_RADIUS);
  const gradient = context.createRadialGradient(0, 0, 30, 0, 0, REACTOR_FIELD_RADIUS);
  gradient.addColorStop(0, "rgba(135, 250, 255, 0.13)");
  gradient.addColorStop(0.42, "rgba(53, 240, 255, 0.08)");
  gradient.addColorStop(1, "rgba(5, 8, 11, 0)");
  context.fillStyle = gradient;
  context.beginPath();
  context.arc(0, 0, REACTOR_FIELD_RADIUS, 0, Math.PI * 2);
  context.fill();
  reactorFieldCache = canvas;
  return canvas;
}

function drawShell(context: CanvasRenderingContext2D, shell: ShellState, palette: ShellSkinPalette, overdriveActive: boolean, overdriveZoneMultiplier: number): void {
  context.save();
  context.lineWidth = shell.thickness;
  context.lineCap = "round";
  context.strokeStyle = palette.shell[Math.min(shell.index, palette.shell.length - 1)];
  context.shadowBlur = 18;
  context.shadowColor = shell.index === 1 ? "rgba(255, 59, 167, 0.25)" : palette.shadow;

  const gateStep = 360 / shell.gateCount;
  const gateHalf = shell.gateDegrees / 2;
  for (let gateIndex = 0; gateIndex < shell.gateCount; gateIndex += 1) {
    const previousGate = shell.angleDegrees + (gateIndex - 1) * gateStep + gateHalf;
    const nextGate = shell.angleDegrees + gateIndex * gateStep - gateHalf;
    context.beginPath();
    context.arc(0, 0, shell.radius, degreesToRadians(previousGate), degreesToRadians(nextGate));
    context.stroke();
    drawGateLip(context, shell, shell.angleDegrees + gateIndex * gateStep, palette);
  }
  drawOverdriveZones(context, shell, overdriveActive, overdriveZoneMultiplier);
  drawPolarityWindows(context, shell);
  context.restore();
}

function drawOverdriveZones(context: CanvasRenderingContext2D, shell: ShellState, active: boolean, multiplier: number): void {
  if (!active || shell.index !== 0) {
    return;
  }

  context.save();
  context.lineCap = "round";
  context.lineWidth = shell.thickness + 9;
  context.strokeStyle = "rgba(255, 211, 74, 0.86)";
  context.shadowBlur = 24;
  context.shadowColor = "rgba(255, 59, 167, 0.54)";
  for (const center of overdriveZoneCenters(shell)) {
    context.beginPath();
    context.arc(0, 0, shell.radius, degreesToRadians(center - OVERDRIVE_RULES.zoneHalfDegrees), degreesToRadians(center + OVERDRIVE_RULES.zoneHalfDegrees));
    context.stroke();
  }

  if (multiplier > 2) {
    context.lineWidth = 3;
    context.strokeStyle = "rgba(233, 251, 255, 0.72)";
    for (const center of overdriveZoneCenters(shell)) {
      context.beginPath();
      context.arc(0, 0, shell.radius + 13, degreesToRadians(center - OVERDRIVE_RULES.zoneHalfDegrees + 2), degreesToRadians(center + OVERDRIVE_RULES.zoneHalfDegrees - 2));
      context.stroke();
    }
  }
  context.restore();
}

function drawPolarityWindows(context: CanvasRenderingContext2D, shell: ShellState): void {
  if (shell.polarityWindows.length === 0) {
    return;
  }

  context.save();
  context.lineCap = "round";
  context.lineWidth = shell.thickness + 5;
  context.shadowBlur = 16;
  for (const window of shell.polarityWindows) {
    drawPolarityWindow(context, shell, window);
  }
  context.restore();
}

function drawPolarityWindow(context: CanvasRenderingContext2D, shell: ShellState, window: ShellPolarityWindow): void {
  const color = coreColors[window.color];
  const center = polarityWindowCenterDegrees(shell, window);
  context.strokeStyle = colorWithAlpha(color, 0.9);
  context.shadowColor = colorWithAlpha(color, 0.45);
  context.beginPath();
  context.arc(0, 0, shell.radius, degreesToRadians(center - window.halfDegrees), degreesToRadians(center + window.halfDegrees));
  context.stroke();
}

function drawGateLip(context: CanvasRenderingContext2D, shell: ShellState, angleDegrees: number, palette: ShellSkinPalette): void {
  const angle = degreesToRadians(angleDegrees);
  const inner = shell.radius - shell.thickness * 1.25;
  const outer = shell.radius + shell.thickness * 1.25;
  context.save();
  context.strokeStyle = palette.gateLip;
  context.lineWidth = 4;
  context.shadowBlur = 14;
  context.shadowColor = "rgba(135, 250, 255, 0.4)";
  context.beginPath();
  context.moveTo(Math.cos(angle) * inner, Math.sin(angle) * inner);
  context.lineTo(Math.cos(angle) * outer, Math.sin(angle) * outer);
  context.stroke();
  context.restore();
}

function drawCore(context: CanvasRenderingContext2D, core: CoreState, polarityStackCap: number, patternId: string): void {
  const color = coreColors[core.color];
  const overcharged = core.isOvercharged;
  context.save();
  context.translate(core.position.x, core.position.y);
  context.rotate(core.spinRadians);
  context.translate(-core.position.x, -core.position.y);
  drawCoreSprite(context, core, color);
  drawCorePattern(context, core, patternId, color);
  context.restore();

  context.save();
  drawCorePolarityOutline(context, core, color, polarityStackCap);
  if (overcharged) {
    drawOverchargedColorMark(context, core, color);
  }
  context.restore();
}

function drawCoreSprite(context: CanvasRenderingContext2D, core: CoreState, color: string): void {
  if (!coreSpriteImage.complete || coreSpriteImage.naturalWidth === 0) {
    drawCoreFallback(context, core, color);
    return;
  }

  const spriteCellSize = coreSpriteImage.naturalWidth / CORE_SPRITE_COLUMNS;
  const sourceX = coreSpriteIndex[core.color] * spriteCellSize;
  const sourceY = (coreSpriteImage.naturalHeight - spriteCellSize) / 2;
  const drawSize = core.radius * 3.4;
  context.save();
  context.shadowBlur = core.isOvercharged ? 28 : 16;
  context.shadowColor = core.isOvercharged ? "rgba(255, 211, 74, 0.62)" : colorWithAlpha(color, 0.42);
  context.drawImage(coreSpriteImage, sourceX, sourceY, spriteCellSize, spriteCellSize, core.position.x - drawSize / 2, core.position.y - drawSize / 2, drawSize, drawSize);
  context.restore();
}

function drawCoreFallback(context: CanvasRenderingContext2D, core: CoreState, color: string): void {
  const { x, y } = core.position;
  const radius = core.radius;
  const gradient = context.createRadialGradient(x - radius * 0.28, y - radius * 0.28, 2, x, y, radius);
  gradient.addColorStop(0, "#ffffff");
  gradient.addColorStop(0.45, core.isOvercharged ? "#ffd34a" : color);
  gradient.addColorStop(1, "rgba(233, 251, 255, 0.12)");
  context.save();
  context.fillStyle = gradient;
  context.shadowBlur = core.isOvercharged ? 34 : 22;
  context.shadowColor = core.isOvercharged ? "rgba(255, 211, 74, 0.65)" : color;
  context.beginPath();
  context.arc(x, y, radius, 0, Math.PI * 2);
  context.fill();
  context.restore();
}

function drawCorePolarityOutline(context: CanvasRenderingContext2D, core: CoreState, color: string, polarityStackCap: number): void {
  if (core.polarityStacks <= 0) {
    return;
  }

  const maxed = core.polarityStacks >= polarityStackCap;
  context.save();
  context.lineWidth = maxed ? 4 : 2;
  context.strokeStyle = colorWithAlpha(color, maxed ? 0.96 : 0.54);
  context.shadowBlur = maxed ? 22 : 10;
  context.shadowColor = colorWithAlpha(color, 0.7);
  context.beginPath();
  context.arc(core.position.x, core.position.y, core.radius + (maxed ? 5 : 3), 0, Math.PI * 2);
  context.stroke();
  context.restore();
}

function drawOverchargedColorMark(context: CanvasRenderingContext2D, core: CoreState, color: string): void {
  const size = core.radius * 0.42;
  context.save();
  context.fillStyle = color;
  context.shadowBlur = 8;
  context.shadowColor = colorWithAlpha(color, 0.7);
  context.beginPath();
  context.moveTo(core.position.x, core.position.y - size);
  context.lineTo(core.position.x + size, core.position.y);
  context.lineTo(core.position.x, core.position.y + size);
  context.lineTo(core.position.x - size, core.position.y);
  context.closePath();
  context.fill();
  context.restore();
}

function renderFeedback(layer: HTMLElement, snapshot: GameSnapshot, seenFeedbackId: number): number {
  let highestId = seenFeedbackId;
  for (const event of snapshot.feedback) {
    if (event.id <= seenFeedbackId || event.frameX === undefined || event.frameY === undefined) {
      continue;
    }

    const element = document.createElement("span");
    element.className = `of-feedback of-feedback-${event.kind}`;
    element.style.left = `${(event.frameX / GAME_FRAME.width) * 100}%`;
    element.style.top = `${(event.frameY / GAME_FRAME.height) * 100}%`;
    element.dataset.color = event.color ?? "white";
    element.textContent = event.text ?? "";
    layer.append(element);
    window.setTimeout(() => element.remove(), 1200);
    highestId = Math.max(highestId, event.id);
  }
  return highestId;
}

function degreesToRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

function colorWithAlpha(hexColor: string, alpha: number): string {
  const key = `${hexColor}:${alpha}`;
  const cached = colorAlphaCache.get(key);
  if (cached) {
    return cached;
  }

  const red = Number.parseInt(hexColor.slice(1, 3), 16);
  const green = Number.parseInt(hexColor.slice(3, 5), 16);
  const blue = Number.parseInt(hexColor.slice(5, 7), 16);
  const color = `rgba(${red}, ${green}, ${blue}, ${alpha})`;
  colorAlphaCache.set(key, color);
  return color;
}
